
import React from "react";
import { WorkoutTableHeader } from "./components/WorkoutTableHeader";
import { DayTabs } from "./components/DayTabs";
import { useWorkoutState } from "./hooks/useWorkoutState";
import { useWorkoutActions } from "./hooks/useWorkoutActions";
import { ClockIcon } from "lucide-react";

export default function WorkoutTable() {
  // Use our custom hooks to manage state and actions
  const state = useWorkoutState();
  const actions = useWorkoutActions(state);
  
  if (state.loading) {
    return (
      <div className="w-full p-4 border rounded-lg shadow-sm bg-white/10 text-white border-white/20">
        <div className="text-center py-8">
          <p>Loading workout plan...</p>
        </div>
      </div>
    );
  }

  // Show waiting message when waiting for data
  if (state.waitingForData) {
    return (
      <div className="w-full p-8 border rounded-lg shadow-sm bg-white/10 text-white border-white/20">
        <div className="text-center py-12 space-y-4">
          <div className="flex justify-center">
            <ClockIcon className="h-12 w-12 text-blue-300 animate-pulse" />
          </div>
          <h3 className="text-xl font-semibold text-white">Program is being generated</h3>
          <p className="text-white/80 max-w-md mx-auto">
            This can take up to a minute depending on the complexity. 
            Please check back soon!
          </p>
          <p className="text-white/60 text-sm">
            The page will automatically update once the program is ready.
          </p>
        </div>
      </div>
    );
  }
  
  // Show empty weeks message when no data but not waiting
  if (!state.currentWeek || state.weeks.length === 0) {
    return (
      <div className="w-full p-4 border rounded-lg shadow-sm bg-white/10 text-white border-white/20">
        <div className="text-center py-8">
          <p>No workout plan data available.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="w-full p-4 border rounded-lg shadow-sm bg-white/10 text-white border-white/20">
      <WorkoutTableHeader
        weeks={state.weeks}
        currentWeekIndex={state.currentWeekIndex}
        currentDayIndex={state.currentDayIndex}
        setCurrentWeekIndex={state.setCurrentWeekIndex}
        onPreviousWeek={actions.handlePrevWeek}
        onNextWeek={actions.handleNextWeek}
        handleUndo={actions.handleUndo}
        handleRedo={actions.handleRedo}
        historyLength={state.history.length}
        redoStackLength={state.redoStack.length}
      />

      <DayTabs
        currentWeek={state.currentWeek}
        activeCell={state.activeCell}
        editMode={state.editMode}
        tempValue={state.tempValue}
        forceRender={state.forceRender}
        setForceRender={state.setForceRender}
        currentDayIndex={state.currentDayIndex}
        setCurrentDayIndex={state.setCurrentDayIndex}
        columns={actions.columns}
        setHasEdited={state.setHasEdited}
        finishEditingWithSave={actions.finishEditingWithSave}
        setActiveCell={state.setActiveCell}
        startEditing={actions.startEditing}
        handleCellClick={actions.handleCellClick}
        handleKeyDown={actions.handleKeyDown}
        inputRefs={actions.inputRefs}
        handleNotesChange={actions.handleNotesChange}
        setTempValue={state.setTempValue}
      />
    </div>
  );
}
